import { isApiError } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Chip, Icon, Kbd, Progress, toast } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { usePermissions } from "../../../session/permissions";
import { useMe } from "../../../session/use-me";
import { useWorkspaceContext, useWorkspaceId } from "../../../session/workspace-context";
import { OFFER_REFETCH_AGAIN_MS, OFFER_SHORTCUTS, OFFER_TICK_MS, concerns, isOfferEvent, offerShortcut, secondsLeft, totalSeconds } from "./offers-model";
import { useInboxStream, type InboxStreamEvent } from "./use-inbox-stream";
import { useNow } from "./use-now";
import { offerKeys, useAnswerOffer, useMyOffers } from "./use-offers";
import { isLiveChatOff, useLiveChatState } from "./use-presence";

interface OfferRow {
  offer_id: string;
  conversation_id: string;
  offered_at: string;
  expires_at: string;
  attempt: number;
  team: string;
  reason: string;
  summary: string;
  channel: string;
}

const CUE_HZ = [880, 1175] as const;
const CUE_STEP_S = 0.14;

/** A short two-note chime for a new offer. Browsers keep audio locked until the page has had a click or key press: then it is silent and the visual cue carries the offer. */
function playCue(): void {
  try {
    const Context = window.AudioContext;
    if (typeof Context !== "function") return;
    const context = new Context();
    if (context.state === "suspended") {
      void context.close();
      return;
    }
    CUE_HZ.forEach((hz, index) => {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = context.currentTime + index * CUE_STEP_S;
      oscillator.frequency.value = hz;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.12, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + CUE_STEP_S);
      oscillator.connect(gain).connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + CUE_STEP_S);
    });
    window.setTimeout(() => { void context.close(); }, 600);
  } catch {
    // no audio device or the browser refused: the visual cue is enough
  }
}

interface CardProps {
  offer: OfferRow;
  left: number;
  busy: boolean;
  onAnswer: (answer: "accept" | "decline") => void;
}

function OfferCard({ offer, left, busy, onAnswer }: CardProps) {
  const { t } = useTranslation();
  const total = totalSeconds(offer.offered_at, offer.expires_at);
  return (
    <section
      aria-label={t("routingLive.offers.title")}
      tabIndex={-1}
      data-testid="offer-toast"
      className="pointer-events-auto w-full rounded-lg border border-brand-500 bg-surface p-4 shadow-lg ring-4 ring-brand-500/20 animate-rise motion-reduce:animate-none"
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="grid size-9 shrink-0 place-items-center rounded-pill bg-brand-50 text-brand-600">
          <Icon name="headset" className="size-5" />
        </span>
        <div className="min-w-0 flex-1 space-y-1">
          <h2 className="text-sm font-bold text-fg">{t("routingLive.offers.title")}</h2>
          <div className="flex flex-wrap items-center gap-1.5">
            {offer.team ? <Chip tone="muted" icon="users">{t("routingLive.offers.team", { team: offer.team })}</Chip> : null}
            {offer.attempt > 1 ? <Chip tone="muted">{t("routingLive.offers.attempt", { n: offer.attempt })}</Chip> : null}
          </div>
        </div>
        <span className="shrink-0 text-sm font-bold tabular-nums text-fg" data-testid="offer-countdown">{t("routingLive.offers.secondsLeft", { seconds: left })}</span>
      </div>
      {offer.summary ? <p className="mt-3 line-clamp-3 text-sm text-fg-muted" data-testid="offer-summary">{offer.summary}</p> : null}
      <Progress value={left} max={total} size="sm" label={t("routingLive.offers.secondsLeft", { seconds: left })} className="mt-3" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" loading={busy} onClick={() => { onAnswer("accept"); }} data-testid="offer-accept">
          <Icon name="check-circle" />
          {t("routingLive.offers.accept")}
        </Button>
        <Button size="sm" variant="secondary" disabled={busy} onClick={() => { onAnswer("decline"); }} data-testid="offer-decline">
          {t("routingLive.offers.decline")}
        </Button>
        <span className="ml-auto hidden items-center gap-1 text-xs text-fg-muted sm:flex">
          <Kbd>{OFFER_SHORTCUTS.accept.label}</Kbd>
          <Kbd>{OFFER_SHORTCUTS.decline.label}</Kbd>
        </span>
      </div>
    </section>
  );
}

/**
 * The offer toast (M29 HND-005, routing pin 4): a chat offered to the signed-in member appears top right with a chime, a
 * countdown and Accept / Pass, and the keyboard answers it (`Alt+Shift+A` accepts the soonest, `Alt+Shift+D` passes it). It
 * is a polite live region, never steals focus and does not animate under reduced motion. Offers live in the database: the
 * list is refetched on every inbox event, on a 10 s poll and on mount, so a reload or an API restart loses nothing. Hidden
 * where `live_chat_ops` is off (the rollout probe says so, so no flagged route is requested and the console logs no 404) and
 * for members who cannot reply to chats.
 */
export function OfferToast() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const canChat = permissions.can("inbox.reply", { anywhere: true }) && !permissions.readOnly;
  const me = useMe();
  const userId = me.data?.user.id;
  const ws = useWorkspaceId();
  const queryClient = useQueryClient();
  const state = useLiveChatState(canChat);
  const on = state.data?.enabled === true;
  const offers = useMyOffers(canChat && on);
  const answer = useAnswerOffer();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const now = useNow(OFFER_TICK_MS);
  const off = isLiveChatOff(offers.error);
  const seen = useRef<ReadonlySet<string>>(new Set());
  const again = useRef<number | undefined>(undefined);

  const refresh = useCallback(() => queryClient.invalidateQueries({ queryKey: offerKeys.mine(ws) }), [queryClient, ws]);
  const onEvent = useCallback(
    (event: InboxStreamEvent) => {
      if (!isOfferEvent(event.type) || !concerns(event, userId)) return;
      void refresh();
      if (event.type === "offer_created") {
        window.clearTimeout(again.current);
        again.current = window.setTimeout(() => { void refresh(); }, OFFER_REFETCH_AGAIN_MS);
      }
    },
    [refresh, userId],
  );
  useInboxStream(onEvent, canChat && on && !off);
  useEffect(() => () => { window.clearTimeout(again.current); }, []);

  const offsetMs = offers.data?.offsetMs ?? 0;
  const live = (offers.data?.offers ?? []).filter((offer) => secondsLeft(offer.expires_at, offsetMs, now) > 0);
  const first = live[0];

  useEffect(() => {
    const fresh = live.filter((offer) => !seen.current.has(offer.offer_id));
    if (fresh.length === 0) return;
    seen.current = new Set([...seen.current, ...fresh.map((offer) => offer.offer_id)]);
    playCue();
  }, [live]);

  const respond = useCallback(
    (offer: OfferRow, choice: "accept" | "decline") => {
      answer.mutate({ conversationId: offer.conversation_id, answer: choice }, {
        onSuccess: () => {
          if (choice === "accept") {
            toast.success(t("routingLive.offers.taken"));
            void navigate(consoleRoutes.inbox(summary.slug, offer.conversation_id));
          } else {
            toast.info(t("routingLive.offers.passed"));
          }
        },
        onError: (error) => {
          const code = isApiError(error) ? error.code : "";
          toast.error(t(code === "offer_expired" ? "routingLive.offers.gone" : code === "at_chat_limit" ? "routingLive.offers.atLimit" : "routingLive.offers.answerFailed"));
        },
      });
    },
    [answer, navigate, summary.slug, t],
  );

  const firstRef = useRef<OfferRow | undefined>(first);
  const respondRef = useRef(respond);
  useEffect(() => {
    firstRef.current = first;
    respondRef.current = respond;
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const choice = offerShortcut(event);
      const target = firstRef.current;
      if (!choice || !target) return;
      event.preventDefault();
      respondRef.current(target, choice);
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); };
  }, []);

  if (!canChat || !on || off) return null;
  const announcement = first ? t("routingLive.offers.announce", { seconds: totalSeconds(first.offered_at, first.expires_at) }) : "";
  return createPortal(
    <div role="region" aria-label={t("routingLive.offers.region")} className="pointer-events-none fixed inset-x-4 top-16 z-50 flex flex-col items-stretch gap-2 sm:left-auto sm:right-4 sm:w-96">
      <div role="status" aria-live="polite" className="sr-only" data-testid="offer-announcer">{announcement}</div>
      {live.map((offer) => (
        <OfferCard key={offer.offer_id} offer={offer} left={secondsLeft(offer.expires_at, offsetMs, now)} busy={answer.isPending && answer.variables.conversationId === offer.conversation_id} onAnswer={(choice) => { respond(offer, choice); }} />
      ))}
    </div>,
    document.body,
  );
}
